/**
 * The `pipeline-mgmt-api` grouping-rules port — the authoring surface for the
 * `tx_grouping` engine's declarative relationship-spec overrides.
 *
 * The engine resolves a precedence ladder per transaction (system default →
 * customer → org → tenant → team → user) and applies **one winner per
 * `saga_kind`**: a tenant rule REPLACES the org rule it shadows rather than
 * blending with it, because two enabled rules on one kind would attach the same
 * leg to two groups. That is the single most important thing for an author to
 * understand, and it is why the list surfaces `scope` and `priority` next to
 * every row.
 *
 * `matchCount` / `lastMatchedAt` are what let this surface answer "is this rule
 * working at all". A rule whose `spec` fails to validate is skipped by the
 * engine with a server-side log and stays `enabled: true`, so without the
 * counters a silently-broken rule and a perfectly-working one look identical.
 *
 * Speaks canonical `/grouping/rules` (mapped to `private/pipeline-mgmt-api`).
 */
import { type ApiClient, unwrapData } from "@authe/fe-commons/api";
import {
  asNullableString,
  asOptString,
  asRecord,
  asString,
  asStringList,
  rowsFromWire,
} from "./wire";
import { walkPages } from "./pagedList";

/**
 * Precedence tiers, most general first — the ladder the engine resolves.
 *
 * The copy-ladder rungs (`commons.tenancy.ladder.RUNG_ORDER`), `system` for its
 * `global`. There is no `team`: a rung names a storage partition, and team
 * membership is not a segment of a tenancy quadruple — sharing is the ABAC
 * carrier `visible_to_team_ids`.
 *
 * It was offered here, and `tx_grouping.config.store._applies_to` narrowed only
 * `user` rows, so a "team" rule was in force for the whole tenancy while
 * `_specificity` ranked it above the tenant rule it shadowed. The picker's help
 * text said "This team, overriding the tenant rule"; both halves were wrong.
 */
export const GROUPING_RULE_SCOPES = [
  "system",
  "customer",
  "org",
  "tenant",
  "user",
] as const;
export type GroupingRuleScope = (typeof GROUPING_RULE_SCOPES)[number];

export interface GroupingRuleSummary {
  id: string;
  name: string;
  description?: string | null;
  scope: GroupingRuleScope;
  enabled: boolean;
  /** Lower wins when several rules target the same `sagaKind`. */
  priority: number;
  sagaKind?: string | null;
  relationshipType?: string | null;
  chainIds: string[];
  /** The engine's `RelationshipSpec` body, as authored. */
  spec: Record<string, unknown>;
  /** Times the engine credited a formed group to this rule. */
  matchCount: number;
  /** Unix seconds of the most recent match, when there has been one. */
  lastMatchedAt?: number | null;
  updatedAt?: number | null;
  updatedBy?: string | null;
}

export interface SaveGroupingRuleInput {
  name: string;
  description?: string | null;
  scope?: GroupingRuleScope;
  enabled?: boolean;
  priority?: number;
  sagaKind?: string | null;
  chainIds?: string[];
  spec?: Record<string, unknown>;
}

/** One selectable option, as the backend labels it. */
export interface CatalogOption {
  value: string;
  label: string;
  description?: string;
}

/** A model field the builder renders a control for. */
export interface CatalogField {
  name: string;
  /** Coarse wire type: `string` / `integer` / `boolean` / `string[]`. */
  type: string;
  required: boolean;
  /** The model's own prose, one sentence, markup stripped. */
  description?: string;
}

/**
 * The authoring vocabulary, read off the live models by
 * `commons.grouping.spec_catalog` and served at `GET /grouping/spec-catalog`.
 *
 * Fetched rather than hardcoded on purpose: a frontend carrying its own
 * `["deterministic", "semi_deterministic", "probabilistic"]` silently omits the
 * fourth linkage model the day somebody adds one, and keeps offering a removed
 * one until a human notices.
 */
/** The address-flow vocabulary: grouping by who moved value to whom. */
export interface FlowCatalog {
  /** The `RelationshipType` a flow spec carries (`address_flow`). */
  relationshipType: string;
  matches: CatalogOption[];
  directions: CatalogOption[];
  groupBys: CatalogOption[];
  fields: CatalogField[];
  /** How `assets` spells the chain's own coin. */
  nativeAsset: string;
  maxAddresses: number;
}

export interface SpecCatalog {
  relationshipTypes: CatalogOption[];
  linkageModels: CatalogOption[];
  truthLocations: CatalogOption[];
  transforms: CatalogOption[];
  defaultTransform: string;
  roles: { canonical: string[]; known: string[]; defaultCompleteRoles: string[] };
  specFields: CatalogField[];
  selectorFields: CatalogField[];
  valueSourceFields: CatalogField[];
  /** `null` against a backend that predates address flows. */
  flow: FlowCatalog | null;
}

export interface GroupingRulesSource {
  /** The authoring vocabulary. Static — cache it for the session. */
  specCatalog(): Promise<SpecCatalog>;
  list(): Promise<GroupingRuleSummary[]>;
  create(input: SaveGroupingRuleInput): Promise<GroupingRuleSummary>;
  update(id: string, input: Partial<SaveGroupingRuleInput>): Promise<GroupingRuleSummary>;
  remove(id: string): Promise<void>;
}

function num(value: unknown, fallback = 0): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function ruleFromWire(raw: Record<string, unknown>): GroupingRuleSummary {
  return {
    id: asString(raw.id),
    name: asString(raw.name),
    description: asNullableString(raw.description),
    scope: asString(raw.scope, "tenant") as GroupingRuleScope,
    enabled: raw.enabled !== false,
    priority: num(raw.priority, 100),
    sagaKind: asNullableString(raw.saga_kind),
    relationshipType: asNullableString(raw.relationship_type),
    chainIds: asStringList(raw.chain_ids),
    spec: asRecord(raw.spec),
    matchCount: num(raw.match_count, 0),
    lastMatchedAt: typeof raw.last_matched_at === "number" ? raw.last_matched_at : null,
    updatedAt: typeof raw.updated_at === "number" ? raw.updated_at : null,
    updatedBy: asNullableString(raw.updated_by),
  };
}

function toWire(input: Partial<SaveGroupingRuleInput>): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  if (input.name !== undefined) body.name = input.name;
  if (input.description !== undefined) body.description = input.description;
  if (input.scope !== undefined) body.scope = input.scope;
  if (input.enabled !== undefined) body.enabled = input.enabled;
  if (input.priority !== undefined) body.priority = input.priority;
  if (input.sagaKind !== undefined) body.saga_kind = input.sagaKind;
  if (input.chainIds !== undefined) body.chain_ids = input.chainIds;
  if (input.spec !== undefined) body.spec = input.spec;
  return body;
}

function optionsFromWire(raw: unknown): CatalogOption[] {
  return rowsFromWire(raw).map((o) => ({
    value: asString(o.value),
    label: asString(o.label ?? o.value),
    description: asOptString(o.description),
  }));
}

function fieldsFromWire(raw: unknown): CatalogField[] {
  return rowsFromWire(raw).map((f) => ({
    name: asString(f.name),
    type: asString(f.type, "string"),
    required: f.required === true,
    description: asOptString(f.description),
  }));
}

function catalogFromWire(raw: Record<string, unknown>): SpecCatalog {
  const roles = asRecord(raw.roles);
  return {
    relationshipTypes: optionsFromWire(raw.relationship_types),
    linkageModels: optionsFromWire(raw.linkage_models),
    truthLocations: optionsFromWire(raw.truth_locations),
    transforms: optionsFromWire(raw.transforms),
    defaultTransform: asString(raw.default_transform, "lower"),
    roles: {
      canonical: asStringList(roles.canonical),
      known: asStringList(roles.known),
      defaultCompleteRoles: asStringList(roles.default_complete_roles),
    },
    specFields: fieldsFromWire(raw.spec_fields),
    selectorFields: fieldsFromWire(raw.selector_fields),
    valueSourceFields: fieldsFromWire(raw.value_source_fields),
    flow: flowCatalogFromWire(raw.flow),
  };
}

function flowCatalogFromWire(raw: unknown): FlowCatalog | null {
  const flow = asRecord(raw);
  const relationshipType = asString(flow.relationship_type);
  if (!relationshipType) return null;
  return {
    relationshipType,
    matches: optionsFromWire(flow.matches),
    directions: optionsFromWire(flow.directions),
    groupBys: optionsFromWire(flow.group_bys),
    fields: fieldsFromWire(flow.fields),
    nativeAsset: asString(flow.native_asset, "native"),
    maxAddresses: num(flow.max_addresses, 500),
  };
}

export function createApiGroupingRulesSource(client: ApiClient): GroupingRulesSource {
  /**
   * Drop the rules list after every write.
   *
   * There was NO invalidation here at all, and the rules page derives which rule
   * the engine applies from the list it was handed — so for 30s after a save the
   * page named the pre-write rule as the one in effect, which is the same lie as
   * the write silently failing.
   *
   * `invalidateMatching`, because `list` walks pages: every read carries
   * `?limit=100` (and a `cursor` past the first page), and the exact-URL
   * `invalidate` keys on the resolved URL query string included.
   */
  const invalidate = (): void => client.invalidateMatching("/grouping/rules");

  return {
    async specCatalog(): Promise<SpecCatalog> {
      const res = await client.get<unknown>("/grouping/spec-catalog");
      return catalogFromWire(asRecord(unwrapData(res)));
    },

    async list(): Promise<GroupingRuleSummary[]> {
      // Paged, for the same reason `pipelinesSource.listDefinitions` is: the
      // route defaults to 50 and returns a `next_cursor` this dropped, and it
      // filters for visibility AFTER taking the page — so a page comes back
      // short, or empty, with more behind it.
      //
      // It matters more here than on a read-only list. This is the RULES
      // management page: a rule missing from it is not "scroll for more", it is
      // a rule somebody recreates — and two grouping rules matching the same
      // shape both fire.
      return (await walkPages(client, "/grouping/rules")).map(ruleFromWire);
    },

    async create(input: SaveGroupingRuleInput): Promise<GroupingRuleSummary> {
      const res = await client.post<unknown>("/grouping/rules", toWire(input));
      const saved = ruleFromWire(asRecord(unwrapData(res)));
      invalidate();
      return saved;
    },

    async update(
      id: string,
      input: Partial<SaveGroupingRuleInput>,
    ): Promise<GroupingRuleSummary> {
      const res = await client.patch<unknown>(
        `/grouping/rules/${encodeURIComponent(id)}`,
        toWire(input),
      );
      const saved = ruleFromWire(asRecord(unwrapData(res)));
      invalidate();
      return saved;
    },

    async remove(id: string): Promise<void> {
      // `unwrapData`: the client RESOLVES an `{ok:false}` envelope, so a bare
      // `await` reported "Rule deleted — … is now the rule in effect for that
      // saga kind" on a refusal. That sentence asserts which correlation rule
      // is governing; announcing it off a write that never landed is the worst
      // single claim this surface can make.
      unwrapData(
        await client.delete<unknown>(`/grouping/rules/${encodeURIComponent(id)}`),
      );
      invalidate();
    },
  };
}
